import React from 'react';
import { css } from '@/styled-system/css';
import { carouselItem, combineCarouselClasses } from './carousel.styles';
import type { CarouselItemProps, CarouselResponsiveVariant } from './types';

/**
 * Helper function to resolve responsive variant values
 * Simplified version - in production, this would handle breakpoint matching
 */
function resolveResponsiveValue<T>(value: T | CarouselResponsiveVariant<T> | undefined): T | undefined {
  if (!value) return undefined;

  // Check if it's a responsive variant object (not a primitive value)
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    const responsive = value as Record<string, T | undefined>;
    // Return base value if available, otherwise return the first defined value
    return responsive.base ?? (Object.values(responsive).find((v) => v !== undefined) as T | undefined);
  }
  return value as T;
}

/**
 * CarouselItem - Individual slide component using semantic <li> element
 *
 * Renders as an HTML list item with proper accessibility attributes.
 * Supports responsive width values and prevents shrinking in horizontal scroll.
 */
export const CarouselItem = React.forwardRef<HTMLLIElement, CarouselItemProps>(
  ({ width, children, className, ...restProps }, ref) => {
    const resolvedWidth = resolveResponsiveValue(width);

    // Create dynamic styles for width if provided
    const itemStyles = resolvedWidth
      ? css({
          width: resolvedWidth,
          maxWidth: resolvedWidth,
        })
      : '';

    const combinedClassName = combineCarouselClasses(carouselItem, itemStyles, className);

    return (
      <li ref={ref} className={combinedClassName} {...restProps}>
        {children}
      </li>
    );
  },
);

CarouselItem.displayName = 'CarouselItem';
